'use client';

/**
 * IntentComposerCard — explorer-surface search composer.
 *
 * Parallel implementation. Same data contract the legacy
 * `intent-composer-section.tsx` consumes (`intent_search` BFF,
 * `intent-store` for conversation state + response + history) but
 * a fresh component owned by mode3. Legacy section stays in place
 * for the legacy `intent` mode; mode3 doesn't import it.
 *
 * Differences from the legacy section:
 *   - No `useTabStore.openTab` — cards-everywhere primitive only.
 *     Search success updates intent-store; the canvas paints from
 *     store state via the matrix's `mode3_compose` preset.
 *   - No dev-seed grid button — that was scaffolding for the
 *     legacy mode and never belonged in cards-everywhere.
 *   - Clears `explorerSelection` on submit so the canvas pops out
 *     of any sub-mode (e.g. Add Schedule) and the new search paints.
 *
 * @see klyde_edge/composition/cards/intent.py — content_key="intent_composer"
 *
 * @component_role: Card
 * @component_type: Explorer Card
 * @domain: Explorer / Intent
 * @layer: presentation
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Loader2, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useIntentGrid } from '@/lib/api/hooks';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useExplorerLabels } from '@/lib/stores/total-matrix-store';
import { cn } from '@/lib/utils/cn';
import { processImageForObserve, isValidImageFile } from '@/lib/utils/image-processing';
import { text, surfaceBase } from '@/lib/design/tokens.x';
import type { IntentGridRequest, IntentGridResponse } from '@/lib/api/types';

const MAX_IMAGES = 3;

// A free-text search is a one-line `intent_grid` turn — it routes
// through the same frontier model (Pareto crop + recommend/consider/
// alternative tiers) as a schedule lane. Stable line_sid so the
// in-canvas refine box re-targets the same lane turn over turn; NOT
// the `__default__` sentinel (reserved for the legacy intent_search
// pool, and excluded from grid refinement).
const SEARCH_LINE_SID = 'search';
// Frontier slots for a free-text search. The dominated tail (~15)
// trails after, so the canvas paints ~25-30 tiered cards.
const FREE_TEXT_TOP_K = 12;

interface StagedImage {
  id: string;
  base64: string;
  thumbnail: string;
  filename: string;
  sizeKB: number;
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

export function IntentComposerCard() {
  const L = useExplorerLabels('intent');

  const [query, setQuery] = useState('');
  const [stagedImages, setStagedImages] = useState<StagedImage[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const conversationId = useIntentStore((s) => s.conversationId);
  const setConversationId = useIntentStore((s) => s.setConversationId);
  const history = useIntentStore((s) => s.history);
  const addHistoryTurn = useIntentStore((s) => s.addHistoryTurn);
  const clearSearch = useIntentStore((s) => s.clear);
  const pendingRestoreQuery = useIntentStore((s) => s.pendingRestoreQuery);
  const clearPendingRestore = useIntentStore((s) => s.clearPendingRestore);
  const pendingSeed = useIntentStore((s) => s.pendingSeed);
  const clearPendingSeed = useIntentStore((s) => s.clearPendingSeed);
  const setLoading = useIntentStore((s) => s.setLoading);
  const setGridResponse = useIntentStore((s) => s.setGridResponse);

  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);

  const gridStrategy = useCapabilityStore((s) => s.strategyName('intent', 'grid'));
  const intentGrid = useIntentGrid(gridStrategy ?? 'intent_grid');

  // Restore from history click — store sets pendingRestoreQuery; we
  // pop it into the input and focus.
  useEffect(() => {
    if (pendingRestoreQuery !== null) {
      setQuery(pendingRestoreQuery);
      clearPendingRestore();
      inputRef.current?.focus();
    }
  }, [pendingRestoreQuery, clearPendingRestore]);

  // Seeded search (e.g. "More Like This") — same text+image shape a
  // buyer's own typed/attached search produces, so it runs through the
  // ordinary fresh-turn submit. Submits with `override` values directly
  // rather than waiting for setQuery/setStagedImages to commit and
  // reading them back via handleSearch's closure — on first mount (the
  // normal case here, since switching to mode3 mounts a fresh composer)
  // that two-step handoff raced: a same-pass auto-submit effect would
  // fire against the still-empty pre-seed state and consume its own
  // flag before the real seeded state ever committed, so the search
  // never actually ran and only the prefilled text/image showed.
  // setQuery/setStagedImages here are purely for what the input and
  // thumbnail display, not for feeding the submit.
  useEffect(() => {
    if (pendingSeed !== null) {
      const seed = pendingSeed;
      setQuery(seed.text);
      setStagedImages(
        seed.images.map((base64) => ({
          id: generateId(),
          base64,
          thumbnail: `data:image/jpeg;base64,${base64}`,
          filename: 'reference.jpg',
          sizeKB: Math.round((base64.length * 3) / 4 / 1024),
        })),
      );
      clearPendingSeed();
      handleSearch({ text: seed.text, images: seed.images });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSeed, clearPendingSeed]);

  const addImages = useCallback(
    async (files: File[]) => {
      const available = MAX_IMAGES - stagedImages.length;
      if (available <= 0) {
        toast.warning((L.composer_max_images || '{n} max').replace('{n}', String(MAX_IMAGES)));
        return;
      }
      const filesToAdd = files.filter(isValidImageFile).slice(0, available);
      if (filesToAdd.length === 0) return;

      setIsProcessing(true);
      for (const file of filesToAdd) {
        try {
          const { base64, sizeKB } = await processImageForObserve(file);
          setStagedImages((prev) => [
            ...prev,
            {
              id: generateId(),
              base64,
              thumbnail: URL.createObjectURL(file),
              filename: file.name,
              sizeKB,
            },
          ]);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[intent-composer-card] failed to process image:', file.name, err);
        }
      }
      setIsProcessing(false);
    },
    [stagedImages.length, L.composer_max_images],
  );

  const removeImage = useCallback((id: string) => {
    setStagedImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.thumbnail);
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const clearImages = useCallback(() => {
    stagedImages.forEach((img) => URL.revokeObjectURL(img.thumbnail));
    setStagedImages([]);
  }, [stagedImages]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);
  const handleDragLeave = useCallback(() => setDragActive(false), []);
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      addImages(Array.from(e.dataTransfer.files));
    },
    [addImages],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addImages(Array.from(e.target.files || []));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // `override` lets a caller (the seed effect below) submit with values
  // that haven't committed to `query`/`stagedImages` state yet, instead
  // of racing a render to read them back out of local state.
  const handleSearch = useCallback((override?: { text: string; images: string[] }) => {
    const trimmed = (override?.text ?? query).trim();
    const overrideImages = override?.images;
    const hasQuery = trimmed.length > 0;
    const hasImages = overrideImages ? overrideImages.length > 0 : stagedImages.length > 0;
    if (!hasQuery && !hasImages) return;

    // Clear any explorer sub-mode (e.g. add_schedule) so the canvas
    // paints the search results.
    setExplorerSelection(null);
    setLoading(true);

    // One multi-turn conversation, not two pipelines. The first submit
    // (or the first after "New") starts a fresh grid; every subsequent
    // text submit on the same conversation is a `refine` turn on the
    // search lane — the SAME branch the in-canvas refine box uses, so a
    // follow-up like "brown" reranks the armchair pool instead of doing
    // a bare-word fresh KNN that returns nothing. "New" clears
    // conversationId, so the next submit is fresh again. Image-bearing
    // turns always go fresh — the refine-text branch is text-only, and
    // adding a moodboard is a new visual search.
    // On the fresh path, `extract_constraints` turns NL bounds in the
    // query ("under $3000") into a filter before the frontier crop.
    const isFollowUp = !!conversationId && hasQuery && !hasImages;
    const body: IntentGridRequest = isFollowUp
      ? {
          conversation_id: conversationId,
          intent_sid: '',
          refine: { line_sid: SEARCH_LINE_SID, text: trimmed },
        }
      : {
          conversation_id: conversationId,
          queries: [{ line_sid: SEARCH_LINE_SID, text: trimmed }],
          top_k_per_line: FREE_TEXT_TOP_K,
          extract_constraints: true,
        };
    if (!isFollowUp && hasImages) body.images = overrideImages ?? stagedImages.map((img) => img.base64);

    intentGrid.mutate(body, {
      onSuccess: (data: IntentGridResponse) => {
        setConversationId(data.conversation_id);
        setGridResponse(data);
        addHistoryTurn({
          id: generateId(),
          conversationId: data.conversation_id,
          query: hasQuery ? trimmed : '',
          imageCount: hasImages ? (overrideImages?.length ?? stagedImages.length) : 0,
          timestamp: new Date(),
          resultCount: data.lines.reduce((n, l) => n + l.candidate_pool_size, 0),
          gridResponse: data,
        });
        setQuery('');
        clearImages();
      },
      onError: (error: Error) => {
        setLoading(false);
        toast.error(`${L.composer_search_failed ?? 'Search failed'}: ${error.message}`);
      },
    });
  }, [
    query, stagedImages, conversationId, intentGrid,
    setExplorerSelection, setLoading, setConversationId,
    setGridResponse, addHistoryTurn, clearImages, L.composer_search_failed,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  const handleNewSearch = () => {
    // Full reset, not just the conversation — also clears gridResponse /
    // response / selections so the canvas (intent_results) returns to its
    // empty state. "New" means a blank slate, not a fresh thread over the
    // old results.
    clearSearch();
    setQuery('');
    clearImages();
    inputRef.current?.focus();
  };

  const canSearch = query.trim().length > 0 || stagedImages.length > 0;
  const isLoading = intentGrid.isPending || isProcessing;
  const turnNumber = history.length + 1;
  const showTurnCounter = conversationId !== null && history.length > 0;

  return (
    <div className="flex flex-col gap-2" style={{ fontSize: surfaceBase.canvas }}>
      <div
        className={cn(
          'rounded-md',
          dragActive && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                dragActive
                  ? (L.composer_drop_active ?? 'Drop image…')
                  : (L.composer_placeholder ?? 'Search via Intent…')
              }
              disabled={isLoading}
              className={cn(
                'w-full pl-9 pr-9 py-2 text-sm rounded-md',
                'bg-muted border border-border',
                'focus:outline-none focus:ring-1 focus:ring-primary',
                'placeholder:text-muted-foreground',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            {isLoading && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-primary" />
            )}
          </div>
        </form>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: MAX_IMAGES }).map((_, slotIndex) => {
          const image = stagedImages[slotIndex];
          if (image) {
            return (
              <div key={image.id} className="relative group w-full aspect-square">
                <img
                  src={image.thumbnail}
                  alt={image.filename}
                  className="w-full h-full object-cover rounded border border-border"
                />
                <button
                  type="button"
                  onClick={() => removeImage(image.id)}
                  className={cn(
                    'absolute -top-1 -right-1 w-4 h-4 rounded-full',
                    'bg-destructive text-destructive-foreground',
                    'flex items-center justify-center',
                    'opacity-0 group-hover:opacity-100 transition-opacity',
                    'hover:bg-destructive/90',
                  )}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
                <span className="absolute bottom-0 right-0 text-[8px] bg-background/80 px-0.5 rounded-tl">
                  {image.sizeKB}KB
                </span>
              </div>
            );
          }
          if (slotIndex > stagedImages.length) {
            return <div key={slotIndex} className="w-full aspect-square" />;
          }
          return (
            <button
              key={slotIndex}
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'w-full aspect-square border-2 border-dashed border-muted-foreground/30 rounded',
                'flex items-center justify-center',
                'hover:border-primary hover:bg-accent/50 transition-colors',
              )}
            >
              <Plus className="w-4 h-4 text-muted-foreground" />
            </button>
          );
        })}
      </div>

      <div className={cn('flex items-center gap-2', text.muted)}>
        <ImageIcon className="w-3 h-3 shrink-0" />
        <span className="truncate">
          {(L.composer_images_counter ?? '{current}/{max} images')
            .replace('{current}', String(stagedImages.length))
            .replace('{max}', String(MAX_IMAGES))}
        </span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleSearch()}
          disabled={!canSearch || isLoading}
          className={cn(
            'flex-1 flex items-center justify-center gap-2',
            'px-3 py-2 text-sm rounded',
            'bg-primary text-primary-foreground',
            'hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed',
          )}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isProcessing
                ? (L.composer_processing ?? 'Processing…')
                : (L.composer_searching ?? 'Searching…')}
            </>
          ) : (
            <>
              <Search className="w-4 h-4" />
              {L.composer_search_button ?? 'Search'}
            </>
          )}
        </button>
        {conversationId && (
          <button
            type="button"
            onClick={handleNewSearch}
            disabled={isLoading}
            className={cn(
              'px-3 py-2 text-xs rounded border border-input',
              'text-muted-foreground hover:border-ring',
            )}
          >
            {L.composer_new_search ?? 'New'}
          </button>
        )}
      </div>

      {showTurnCounter && (
        <p className={text.muted}>
          {(L.composer_turn_of_conversation ?? 'Turn {n} of conversation').replace(
            '{n}',
            String(turnNumber),
          )}
        </p>
      )}
    </div>
  );
}
